import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Background from '@/common/Background';
import Button from '@mui/material/Button';
import Logo from '@/common/Logo';
import { Suspense, useRef, useState, lazy } from 'react';
import useCountryConfigurations from '@/hooks/server/useCountryConfigurations';
import LoadingOverlay from '@/common/LoadingOverlay';
import useStore, {
  selectFavoriteCountryId,
  selectShowProductTour,
  selectSetShowProductTour,
} from '@/hooks/client/useStore';
import SearchProduct from './SearchProduct';
import FindBatchCodePreview from '../find-product-batch/FindBatchCodePreview';
import HelpOutlinedIcon from '@mui/icons-material/HelpOutlined';

const VideoSlider = lazy(() => import('./VideoSlider'));
const KeepFresh = lazy(() => import('@/features/keep-fresh/KeepFresh'));
const RecipeSlider = lazy(() => import('../recipe/RecipeSlider'));
const Seal = lazy(() => import('@/features/unique-by-country/Seal'));
const Footer = lazy(() => import('@/common/Footer'));

const css = {
  mainBox: {
    margin: '0 auto',
    p: 1,
    maxWidth: '450px',
  },
  logoWrapper: { display: 'flex', justifyContent: 'center' },
  title: {
    textTransform: 'uppercase',
    mb: '1rem',
    lineHeight: '1.1',
    textAlign: 'center',
    color: '#fff',
    fontSize: '24px',
    fontWeight: '500',
    mt: 2,
  },
  questionsLink: {
    fontWeight: '500',
    fontSize: '0.9rem',
    textDecoration: 'underline',
    color: '#fff',
    px: 1,
  },
  showMoreBox: { display: 'flex', justifyContent: 'center' },
  showMoreIcon: (color) => ({
    backgroundColor: color,
    borderRadius: 50,
    color: 'white',
    fontSize: '2rem',
    width: '2.3rem',
    height: '2.3rem',
    padding: '0.1rem',
    mt: 3,
  }),
  questionsBox: { textAlign: 'center', mt: 2 },
  tourButton: {
    backgroundColor: '#E1251B',
    width: '66px',
    height: '35px',
    color: 'white',
    mt: 1,
    borderRadius: 3,
    fontSize: '14px',
    fontWeight: '400',
    backdropFilter: 'blur(28px)',
    boxShadow: '0px 4px 4px 0px #00000040',
  },
  tourbuttonWrapper: { display: 'flex', justifyContent: 'end' },
  helpIcon: { color: '#fff', mr: '3px' },
};

const SearchView = () => {
  const { isLoading } = useCountryConfigurations();
  const countryCode = useStore(selectFavoriteCountryId);
  const showProductTour = useStore(selectShowProductTour);
  const setShowProductTour = useStore(selectSetShowProductTour);
  const sectionRef = useRef(null);
  const [runTour, setRunTour] = useState(showProductTour);

  const handleRunTour = (status) => {
    setRunTour(status);
    showProductTour && setShowProductTour(false);
  };

  if (isLoading) return <LoadingOverlay />;

  return (
    <Box>
      <Background center={true} height={55}>
        <Box sx={css.mainBox}>
          <Box sx={css.tourbuttonWrapper}>
            <Button sx={css.tourButton} onClick={() => handleRunTour(true)}>
              <HelpOutlinedIcon sx={css.helpIcon} /> Tour
            </Button>
          </Box>
          <Box sx={css.logoWrapper}>
            <Logo sx={css.logo} />
          </Box>
          <Typography variant="h1" sx={css.title}>
            Es natural que te dé confianza saberlo todo
          </Typography>

          {/* Search */}
          <SearchProduct runTour={runTour} handleRunTour={handleRunTour} />

          {/* Where to find the batch code */}
          <FindBatchCodePreview />
        </Box>
      </Background>
      {/* -------- miscellaneous section-------- */}
      <Box ref={sectionRef}>
        {/* video slider */}
        <Suspense fallback={null}>
          <VideoSlider />
        </Suspense>
      </Box>
      {/* recipes */}
      <Suspense fallback={null}>
        <RecipeSlider />
      </Suspense>

      {/* keep fresh */}
      <Suspense fallback={null}>
        <KeepFresh />
      </Suspense>

      {/* Seal */}
      {countryCode === 'CO' && (
        <Suspense fallback={null}>
          <Seal />
        </Suspense>
      )}
      {/* Footer */}
      <Suspense fallback={null}>
        <Footer />
      </Suspense>
    </Box>
  );
};

export default SearchView;
